import React from 'react';
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty';
import { IconButton } from '@mui/material';
import InnerImageZoom from 'react-inner-image-zoom';
import { cssInJS } from '../../../utils/styles';
import PanoramaPhotoViewer from '../panorama-photo-view';

const styles = cssInJS({
  imgContainer: {
    display: 'inline-block',
    maxWidth: '85% !important',
    maxHeight: '85%',
    margin: 'auto auto',
    verticalAlign: 'middle',
    backgroundColor: '#000',
    boxShadow: '0 14px 45px rgba(0,0,0,0.25), 0 10px 18px rgba(0,0,0,0.22)',
    borderRadius: 3,
    objectFit: 'contain',
    position: 'relative',
    // @ts-ignore
    '@phone': {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      boxShadow: 'none',
    },
    figure: {
      display: 'block',
    },
  },
  img: {
    display: 'block',
    width: '100%',
    height: 'auto',
    maxHeight: 'calc(100vh - 275px)',
    objectFit: 'contain',
  },
}) as any;

type Props = {
  imageSrc: string,
  originalSrc: string,
  onResize: () => void,
  isZoomable: boolean,
  photoRef: React.LegacyRef<HTMLDivElement>,
  panoramaEnabled: boolean,
  switchPhotoViewer: () => void,
  dimensions: {
    width: number,
    height: number,
  },
  accessId: string,
  disableRecognition: () => void,
  enableRecognition: () => void,
}

const PhotoView = ({
  imageSrc, originalSrc, onResize, isZoomable, photoRef, panoramaEnabled, switchPhotoViewer, dimensions, accessId,
  disableRecognition, enableRecognition,
}: Props) => {
  const hasPanoramaRatio = dimensions.height * 2 === dimensions.width;

  return (
    <div ref={photoRef} className={styles.imgContainer}>
      {hasPanoramaRatio && panoramaEnabled
        ? <PanoramaPhotoViewer url={imageSrc} accessId={accessId} viewSwitchHandler={switchPhotoViewer} />
        : renderDefaultPhotoView(imageSrc, originalSrc, onResize, isZoomable, disableRecognition, enableRecognition)}
      {hasPanoramaRatio && !panoramaEnabled && (
        <IconButton
          title="View in 360° Mode"
          data-testid="switch-to-360-view-icon"
          onClick={switchPhotoViewer}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            color: '#fff',
          }}
        >
          <ThreeSixtyIcon />
        </IconButton>
      )}
    </div>
  );
};

const renderDefaultPhotoView = (
  imageSrc: string,
  originalSrc: string,
  onResize: () => void,
  isZoomable: boolean,
  disableRecognition: () => void,
  enableRecognition: () => void,
) => {
  if (isZoomable) {
    return (
      <InnerImageZoom
        data-testid="inner-image-zoom-photo"
        zoomSrc={originalSrc}
        src={imageSrc}
        imgAttributes={{ onLoad: onResize, className: styles.img }}
        hideHint
        afterZoomIn={disableRecognition}
        afterZoomOut={enableRecognition}
      />
    );
  }

  return (
    <img
      data-testid="default-img"
      alt=""
      src={imageSrc}
      onLoad={onResize}
      className={styles.img}
    />
  );
};

export default PhotoView;
